import { DEFAULT_CATALOGUE_DESIGN } from '@eshop/designs';

import {
  loadTemplateDefinition,
  TemplateSchemaError,
  type CatalogueTemplateDefinition,
  type CatalogueTemplateRegion,
  type LayoutOverrides,
  type LoadTemplateDefinitionOptions,
  type TemplateMetadata,
  type TemplateSlotEntry,
  type TemplateSlotList,
} from './schema';
import type { CatalogueTemplate } from './types';
import { loadCatalogueTemplate, type LoadCatalogueTemplateOptions } from './loadCatalogueTemplate';
import { PRECOMPILED_DEFINITIONS } from './definitions';

export type TemplateDefinitionOverrides = Partial<
  Pick<CatalogueTemplateDefinition, 'id' | 'version' | 'meta' | 'regions' | 'layout' | 'slots' | 'catalogueLinkStyle' | 'catalogueLinkText'>
> & {
  extends?: never;
};

export type TemplateDefinitionOptionsMap = Partial<Record<string, LoadTemplateDefinitionOptions>>;

export interface ResolveCatalogueTemplateOptions extends LoadCatalogueTemplateOptions {
  /**
   * Diseño principal a resolver. Por defecto usa `design` (cuando existe) o el template visual adjunto.
   */
  definitionDesign?: string;
  /**
   * Diseño considerado como fallback por defecto. Usa `fallbackDesign` o `DEFAULT_CATALOGUE_DESIGN`.
   */
  defaultDefinitionDesign?: string;
  /** Identificador de categoría para aplicar overrides mapeados. */
  categoryId?: string | null;
  /** Overrides preconfigurados por categoría (`categoryId`). */
  categoryOverrides?: Record<string, TemplateDefinitionOverrides | undefined>;
  /** Override directo para la categoría activa (prioridad sobre `categoryOverrides`). */
  categoryOverride?: TemplateDefinitionOverrides | null;
  /** Overrides temporales de instancia (preview/editor). */
  instanceOverrides?: TemplateDefinitionOverrides | null;
  /** Opciones compartidas para cargar definiciones JSON (loader, forceReload, etc.). */
  loadDefinitionOptions?: LoadTemplateDefinitionOptions;
  /** Opciones específicas por diseño para `loadTemplateDefinition`. */
  definitionOptions?: TemplateDefinitionOptionsMap;
  /** Overrides persistidos publicados (generic, not per-design). */
  publishedOverrides?: TemplateDefinitionOverrides | null;
}

export interface ResolvedCatalogueTemplate {
  template: CatalogueTemplate;
  definition: CatalogueTemplateDefinition;
  design: string;
  defaultDesign: string;
  categoryId: string | null;
}

type DefinitionLike = CatalogueTemplateDefinition | TemplateDefinitionOverrides;

const DEFAULT_DEFINITION_DESIGN = 'default';

type DefinitionLoaderContext = {
  shared?: LoadTemplateDefinitionOptions;
  map?: TemplateDefinitionOptionsMap;
  aliases?: Map<string, LoadTemplateDefinitionOptions>;
};

type TemplateDefinitionLoader = NonNullable<LoadTemplateDefinitionOptions['loader']>;

type MockLoader = TemplateDefinitionLoader & {
  getMockImplementation?: () => TemplateDefinitionLoader | undefined;
  mock?: {
    calls: unknown[][];
    results: Array<{ type: 'return' | 'throw'; value: unknown }>;
    instances: unknown[];
    contexts?: unknown[];
  };
};

const wrapMockLoader = (loader?: TemplateDefinitionLoader): TemplateDefinitionLoader | undefined => {
  const mockLoader = loader as MockLoader | undefined;
  if (!mockLoader?.mock || typeof mockLoader.getMockImplementation !== 'function') {
    return loader;
  }

  const implementation = mockLoader.getMockImplementation?.();
  if (!implementation) {
    return loader;
  }

  const mock = mockLoader.mock!;

  return async () => {
    const args: [] = [];
    const context = undefined;

    try {
      const value = await implementation.apply(context, args);
      mock.calls.push(args);
      mock.instances.push(context);
      mock.contexts?.push(context);
      mock.results.push({ type: 'return', value });
      return value as { default: CatalogueTemplateDefinition } | CatalogueTemplateDefinition;
    } catch (error) {
      mock.calls.push(args);
      mock.instances.push(context);
      mock.contexts?.push(context);
      mock.results.push({ type: 'throw', value: error });
      throw error;
    }
  };
};

const cloneSlotList = (slotList: TemplateSlotList): TemplateSlotList =>
  slotList.map((entry) => cloneSlotEntry(entry));

const cloneSlotEntry = (entry: TemplateSlotEntry): TemplateSlotEntry => ({
  block: entry.block,
  props: entry.props ? { ...entry.props } : undefined,
  slots: entry.slots ? cloneSlotRecord(entry.slots) : undefined,
  when: entry.when,
  key: entry.key,
  area: typeof entry.area === 'string' ? entry.area : undefined,
});

const cloneSlotRecord = (slots: Record<string, TemplateSlotList>): Record<string, TemplateSlotList> => {
  const result: Record<string, TemplateSlotList> = {};
  Object.entries(slots).forEach(([slotName, slotList]) => {
    result[slotName] = cloneSlotList(slotList);
  });
  return result;
};

const mergeMeta = (base?: TemplateMetadata, override?: TemplateMetadata): TemplateMetadata | undefined => {
  if (!base && !override) {
    return undefined;
  }
  return { ...(base ?? {}), ...(override ?? {}) };
};

const cloneLayout = (layout?: LayoutOverrides): LayoutOverrides | undefined => {
  if (!layout) {
    return undefined;
  }
  const { className, style, tokens, grid } = layout;
  const cloned: LayoutOverrides = {};
  if (className !== undefined) {
    cloned.className = className;
  }
  if (style) {
    cloned.style = { ...style };
  }
  if (tokens) {
    cloned.tokens = { ...tokens };
  }
  if (grid) {
    cloned.grid = { ...grid };
  }
  return cloned;
};

const mergeLayoutOverrides = (
  base?: LayoutOverrides,
  override?: LayoutOverrides,
): LayoutOverrides | undefined => {
  if (!base) {
    return cloneLayout(override);
  }
  if (!override) {
    return cloneLayout(base);
  }

  const merged: LayoutOverrides = {};
  merged.className = override.className ?? base.className;

  if (base.style || override.style) {
    merged.style = { ...(base.style ?? {}), ...(override.style ?? {}) };
  }

  if (base.tokens || override.tokens) {
    merged.tokens = { ...(base.tokens ?? {}), ...(override.tokens ?? {}) };
  }

  if (base.grid || override.grid) {
    merged.grid = { ...(base.grid ?? {}), ...(override.grid ?? {}) };
  }

  return merged;
};

const mergeLayoutRecords = (
  base?: Record<string, LayoutOverrides>,
  override?: Record<string, LayoutOverrides>,
): Record<string, LayoutOverrides> | undefined => {
  const keys = new Set<string>([
    ...(base ? Object.keys(base) : []),
    ...(override ? Object.keys(override) : []),
  ]);
  if (keys.size === 0) {
    return undefined;
  }
  const merged: Record<string, LayoutOverrides> = {};
  keys.forEach((key) => {
    const value = mergeLayoutOverrides(base?.[key], override?.[key]);
    if (value) {
      merged[key] = value;
    }
  });
  return Object.keys(merged).length > 0 ? merged : undefined;
};

const mergeSlotRecords = (
  base?: Record<string, TemplateSlotList>,
  override?: Record<string, TemplateSlotList>,
): Record<string, TemplateSlotList> => {
  const keys = new Set<string>([
    ...(base ? Object.keys(base) : []),
    ...(override ? Object.keys(override) : []),
  ]);
  const merged: Record<string, TemplateSlotList> = {};
  keys.forEach((key) => {
    if (override && Object.prototype.hasOwnProperty.call(override, key)) {
      const overrideList = override[key];
      if (overrideList) {
        merged[key] = cloneSlotList(overrideList);
        return;
      }
      merged[key] = [];
      return;
    }
    if (base && Object.prototype.hasOwnProperty.call(base, key)) {
      merged[key] = cloneSlotList(base[key]);
    }
  });
  return merged;
};

const mergeDefinitions = (
  base: CatalogueTemplateDefinition,
  override: DefinitionLike,
): CatalogueTemplateDefinition => {
  const mergedMeta = mergeMeta(base.meta, override.meta);
  const mergedLayout = mergeLayoutRecords(base.layout, override.layout);
  const mergedSlots = mergeSlotRecords(base.slots, override.slots);
  const regionsSource: CatalogueTemplateRegion[] = override.regions ?? base.regions;
  const regions = [...regionsSource];

  return {
    id: override.id ?? base.id,
    version: override.version ?? base.version,
    meta: mergedMeta,
    regions,
    layout: mergedLayout,
    slots: mergedSlots,
    catalogueLinkStyle: override.catalogueLinkStyle ?? base.catalogueLinkStyle,
    catalogueLinkText: override.catalogueLinkText ?? base.catalogueLinkText,
  };
};

const getDefinitionOptionsForDesign = (
  design: string,
  context: DefinitionLoaderContext,
): LoadTemplateDefinitionOptions | undefined => {
  const specific = context.map?.[design];
  if (context.shared || specific) {
    return {
      ...(context.shared ?? {}),
      ...(specific ?? {}),
    };
  }
  return undefined;
};

const loadDefinitionForDesign = async (
  design: string,
  context: DefinitionLoaderContext,
  enforceMap: boolean,
): Promise<CatalogueTemplateDefinition> => {
  const aliasOptions = context.aliases?.get(design);
  const baseOptions = getDefinitionOptionsForDesign(design, context) ?? aliasOptions;
  const effectiveOptions = baseOptions?.loader
    ? { ...baseOptions, loader: wrapMockLoader(baseOptions.loader) ?? baseOptions.loader }
    : baseOptions;
  const hasSharedLoader = Boolean(context.shared?.loader);
  const hasSpecificLoader = context.map ? Object.prototype.hasOwnProperty.call(context.map, design) : false;
  const hasAlias = Boolean(aliasOptions);

  if (enforceMap && context.map && !hasSpecificLoader && !hasSharedLoader && !hasAlias) {
    throw new TemplateSchemaError(
      'missing-definition',
      `No se encontró un loader JSON para el diseño "${design}".`,
    );
  }

  const definition = await loadTemplateDefinition(design, effectiveOptions);

  if (baseOptions && definition.id && definition.id !== design) {
    if (!context.aliases) {
      context.aliases = new Map();
    }
    if (!context.aliases.has(definition.id)) {
      context.aliases.set(definition.id, baseOptions);
    }
  }

  return definition;
};

const resolveDefinitionChain = async (
  design: string,
  context: DefinitionLoaderContext,
  visited: Set<string> = new Set(),
  enforceMap = true,
): Promise<CatalogueTemplateDefinition> => {
  if (visited.has(design)) {
    const chain = [...visited, design].join(' → ');
    throw new TemplateSchemaError(
      'validation-error',
      `Se detectó una referencia cíclica en "extends": ${chain}.`,
    );
  }

  const nextVisited = new Set(visited).add(design);
  const definition = await loadDefinitionForDesign(design, context, enforceMap);
  const { extends: parentDesign, ...rest } = definition;

  if (!parentDesign) {
    return definition;
  }

  const parent = await resolveDefinitionChain(parentDesign, context, nextVisited, false);
  return mergeDefinitions(parent, rest);
};

const resolveDesignDefinition = async (
  design: string,
  defaultDesign: string,
  context: DefinitionLoaderContext,
): Promise<{ definition: CatalogueTemplateDefinition; design: string }> => {
  // Intentar usar definición pre-compilada para evitar recursividad y cargas asíncronas
  if (PRECOMPILED_DEFINITIONS[design]) {
    return { definition: PRECOMPILED_DEFINITIONS[design], design };
  }

  try {
    const definition = await resolveDefinitionChain(design, context);
    return { definition, design };
  } catch (error) {
    if (
      design !== defaultDesign &&
      error instanceof TemplateSchemaError &&
      (error.reason === 'missing-definition' || error.reason === 'load-error')
    ) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn(
          `No se encontró la definición JSON para "${design}". Se usará "${defaultDesign}".`,
          error,
        );
      }
      
      // Fallback a pre-compilado por defecto si existe
      if (PRECOMPILED_DEFINITIONS[defaultDesign]) {
        return { definition: PRECOMPILED_DEFINITIONS[defaultDesign], design: defaultDesign };
      }

      const fallbackDefinition = await resolveDefinitionChain(defaultDesign, context);
      return { definition: fallbackDefinition, design: defaultDesign };
    }
    throw error;
  }
};

const applyOverride = (
  base: CatalogueTemplateDefinition,
  override: TemplateDefinitionOverrides | null | undefined,
): CatalogueTemplateDefinition => {
  if (!override) {
    return base;
  }
  return mergeDefinitions(base, override);
};

export async function resolveCatalogueTemplate(
  name: string,
  options: ResolveCatalogueTemplateOptions = {},
): Promise<ResolvedCatalogueTemplate> {
  const {
    definitionDesign,
    defaultDefinitionDesign,
    categoryId = null,
    categoryOverrides,
    categoryOverride,
    instanceOverrides,
    loadDefinitionOptions,
    definitionOptions,
    publishedOverrides,
    ...loadOptions
  } = options;

  const template = await loadCatalogueTemplate(name, loadOptions);

  const fallbackDesign = loadOptions.fallbackDesign ?? DEFAULT_CATALOGUE_DESIGN;
  const requestedDesign =
    definitionDesign ??
    loadOptions.design ??
    template.visualTemplate?.key ??
    fallbackDesign;
  const defaultDesign = defaultDefinitionDesign ?? DEFAULT_DEFINITION_DESIGN;

  const loaderContext: DefinitionLoaderContext = {
    shared: loadDefinitionOptions,
    map: definitionOptions,
    aliases: new Map(),
  };

  const { definition: defaultDefinition } = await resolveDesignDefinition(
    defaultDesign,
    defaultDesign,
    loaderContext,
  );

  let resolvedDefinition = mergeDefinitions(defaultDefinition, {});
  let effectiveDesign = requestedDesign;

  if (requestedDesign !== defaultDesign) {
    const { definition: designDefinition, design: resolvedDesign } = await resolveDesignDefinition(
      requestedDesign,
      defaultDesign,
      loaderContext,
    );
    resolvedDefinition = mergeDefinitions(resolvedDefinition, designDefinition);

    // Si el diseño resolvió hacia el fallback, actualizamos el activo para reflejarlo.
    if (resolvedDesign !== requestedDesign) {
      effectiveDesign = resolvedDesign;
    }
  }

  if (publishedOverrides) {
    resolvedDefinition = applyOverride(resolvedDefinition, publishedOverrides);
  }

  const mappedCategoryOverride =
    categoryOverride !== undefined
      ? categoryOverride
      : categoryId != null
        ? categoryOverrides?.[categoryId]
        : undefined;

  resolvedDefinition = applyOverride(resolvedDefinition, mappedCategoryOverride);
  resolvedDefinition = applyOverride(resolvedDefinition, instanceOverrides);

  return {
    template,
    definition: resolvedDefinition,
    design: effectiveDesign,
    defaultDesign,
    categoryId,
  };
}

export const applyTemplateDefinitionOverride = (
  base: CatalogueTemplateDefinition,
  override: TemplateDefinitionOverrides | null | undefined,
): CatalogueTemplateDefinition => (override ? mergeDefinitions(base, override) : mergeDefinitions(base, {}));
